import React, { useContext, useEffect, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { useAnalyticsContext, useProductListAnalytics } from 'app/shared/utils';
import { City, CityForClp, EventForCompactEventCard } from 'app/shared/typings';
import { AuthContext } from 'app/shared/context/Auth';
import { Box, BoxProps, Responsive } from 'app/shared/components/atoms';
import { BasePageBox } from 'app/shared/components/molecules';
import { NavigationButton } from 'app/shared/components/molecules';
import { GetEventsForFan } from 'app/fan/graphql/events/queryHooks';
import { Filmstrip } from 'app/fan/components/organisms/Filmstrip';

import { Title } from './Title';

interface Props extends BoxProps {
  cityLoading?: boolean;
  cityError?: ApolloError;
  city?: City | CityForClp;
  artistSlug?: string;
  eventCollectionSlug?: string;
  qaId?: string;
  location?: string;
  invertColors: boolean;
  eventsToDisplay?: EventForCompactEventCard[];
  includeCityNameInTitle: boolean;
  onNoEvents?: () => void;
}

const UPCOMING_CONCERTS_DISPLAYED = 'Upcoming Concerts Filmstrip Displayed';
const UPCOMING_CONCERTS_NOT_DISPLAYED =
  'Upcoming Concerts Filmstrip NOT Displayed';

export const FilmstripSection: React.FC<React.PropsWithChildren<Props>> = ({
  cityLoading,
  cityError,
  city,
  artistSlug,
  eventCollectionSlug,
  qaId = 'landing-page-events',
  location = 'landing-page',
  invertColors,
  eventsToDisplay,
  includeCityNameInTitle,
  onNoEvents,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const { user } = useContext(AuthContext);
  const [eventsReady, setEventsReady] = useState(!!eventsToDisplay);
  const { trackAnalyticsEvent } = useAnalyticsContext();
  const { colors } = useCurrentTheme();

  const citySlug = city?.cachedSlug;
  const artistOrCityPresent = !!artistSlug || !!citySlug;

  const shouldSkipQuery =
    !!eventsToDisplay || cityLoading || !!cityError || !artistOrCityPresent;
  const perPage = 12;
  const {
    loading: eventsLoading,
    error: eventsError,
    data: eventsData,
  } = GetEventsForFan({
    city: citySlug,
    confirmedArtist: artistSlug,
    eventCollection: eventCollectionSlug,
    excludeCancelled: true,
    excludeNonPresale: false,
    excludePresale: true,
    excludeSoldOut: true,
    globallyPromoted: true,
    includeNearbySecondaryCities: !!citySlug,
    page: 1,
    perPage,
    published: true,
    upcoming: true,
    skip: shouldSkipQuery,
  });

  const events = eventsToDisplay || eventsData?.events?.events || [];
  const noConcerts = eventsReady && events.length === 0;

  useEffect(() => {
    if (noConcerts && onNoEvents) {
      onNoEvents();
    }
  }, [noConcerts]);

  const doNotDisplay =
    !artistOrCityPresent ||
    cityLoading ||
    !!cityError ||
    eventsLoading ||
    !!eventsError ||
    !eventsReady ||
    noConcerts;

  const { onGetEventsQueryCompleted } = useProductListAnalytics({
    dataLoaded: !doNotDisplay,
    city,
    artistSlug,
    eventCollectionSlug,
  });

  const sharedAnalyticsArgs = {
    nonInteraction: 1,
    artist: artistSlug,
    city: citySlug,
    collection: eventCollectionSlug,
  };

  useEffect(() => {
    if (eventsData?.events) {
      setEventsReady(true);
      onGetEventsQueryCompleted({
        events,
        metadata: eventsData.events.metadata,
        productListType: 'filmstrip',
        perPage,
        appliedFilterKeys: [],
        user,
      });
    }
  }, [eventsData]);
  useEffect(() => {
    if (eventsReady && events.length > 0) {
      trackAnalyticsEvent(UPCOMING_CONCERTS_DISPLAYED, {
        ...sharedAnalyticsArgs,
        total_events_count:
          eventsData?.events?.metadata?.totalRecords || events.length,
        displayed_events_count: events.length,
        location,
      });
    }
  }, [events, eventsReady]);

  function errorMessage() {
    if (cityError) {
      return 'Could not determine closest Sofar city';
    }
    if (eventsError) {
      return eventsError;
    }
    if (
      !cityLoading &&
      !eventsLoading &&
      artistOrCityPresent &&
      (eventsToDisplay !== undefined || eventsData?.events) &&
      noConcerts
    ) {
      return 'No upcoming shows to display';
    }

    return null;
  }
  const error = errorMessage();

  useEffect(() => {
    if (error) {
      trackAnalyticsEvent(UPCOMING_CONCERTS_NOT_DISPLAYED, {
        ...sharedAnalyticsArgs,
        error,
      });
    }
  }, [error]);

  if (doNotDisplay) {
    return null;
  }

  const seeAll = formatMessage({ id: 'shared.seeAll' });

  return (
    <Box
      backgroundColor={invertColors ? colors.primary.black.base : undefined}
      color={invertColors ? colors.primary.white.lightest : undefined}
      pt={[10, 10, 20, 20]}
      {...props}
    >
      <BasePageBox justifyContent="space-between" alignItems="flex-end">
        <Title
          invertColors={invertColors}
          includeCityNameInTitle={includeCityNameInTitle}
          city={city}
          artistSlug={artistSlug}
          qaId={qaId}
        />
        {city && (
          <Responsive showOnSizes={['tablet', 'smallDesktop', 'largeDesktop']}>
            <NavigationButton
              mb={8}
              variant="tertiary"
              href={`/cities/${city.cachedSlug}`}
              data-qaid={`${qaId}-film-strip-cta`}
            >
              {seeAll}
            </NavigationButton>
          </Responsive>
        )}
      </BasePageBox>
      <Filmstrip
        events={events}
        city={city}
        qaId={qaId}
        invertColors={invertColors}
      />
      {city && (
        <Responsive showOnSizes={['mobile']}>
          <Box px={4} pt={0} pb={[10, 0, 0, 0]}>
            <NavigationButton
              variant="primary"
              fullWidth={true}
              href={`/cities/${city.cachedSlug}`}
              data-qaid={`${qaId}-film-strip-cta`}
            >
              {seeAll}
            </NavigationButton>
          </Box>
        </Responsive>
      )}
    </Box>
  );
};
